import { SimpleInput, SimpleSelect } from '@aloft/toolkit-sui';

import { Controller } from 'react-hook-form';

import { Td, Tr } from '../../../../components/table.js';
import { useZodFormContext } from '../../../../hooks/useZodForm.js';
import { schPairedSalesForm } from '../../schemas.js';
import { PairedSalesFormAdjustKey, PairedSalesFormPropertyKey } from '../../types.js';
import { AdjustPairsLabelInput } from './AdjustPairsLabelInput.js';

type AdjustPairsRowProps<T extends PairedSalesFormPropertyKey> = {
  pairIndex: number;
  propertyKey: T;
  adjustKey?: PairedSalesFormAdjustKey;
  label: string;
  labelKey?: 'additionalFeature1Title' | 'additionalFeature2Title' | 'additionalFeature3Title';
  setLabel?: (label: string) => void;
  inputType: 'text' | 'number' | 'select' | 'date' | 'count';
  options?: readonly string[];
};

export const AdjustPairsRow = <T extends PairedSalesFormPropertyKey>({
  pairIndex,
  propertyKey,
  adjustKey,
  setLabel,
  labelKey,
  label,
  inputType,
  options,
}: AdjustPairsRowProps<T>) => {
  const { control } = useZodFormContext<typeof schPairedSalesForm>();

  return (
    <Tr>
      {setLabel && labelKey ? (
        <Td type="editable" width="200px">
          <Controller
            name={`selectedPairs.${pairIndex}.${labelKey}`}
            control={control}
            render={({ field }) => (
              <AdjustPairsLabelInput label={field.value ?? label} setLabel={field.onChange} />
            )}
          />
        </Td>
      ) : (
        <Td type="readonly" width="200px">
          {label}
        </Td>
      )}
      {(['p1', 'p2'] as const).map((p) => (
        <Td key={p} type="editable" width="125px">
          {inputType === 'select' ? (
            <Controller
              name={`selectedPairs.${pairIndex}.${p}.${propertyKey}`}
              control={control}
              render={({ field }) => (
                <SimpleSelect {...field} variant="grid">
                  {options?.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </SimpleSelect>
              )}
            />
          ) : (
            <Controller
              name={`selectedPairs.${pairIndex}.${p}.${propertyKey}`}
              control={control}
              render={({ field }) => (
                <SimpleInput
                  {...field}
                  variant="grid"
                  type={inputType}
                  {...(inputType === 'count' && { min: 0 })}
                />
              )}
            />
          )}
        </Td>
      ))}

      <Td type={adjustKey ? 'editable' : 'readonly'} width="100px">
        {adjustKey && (
          <Controller
            name={`selectedPairs.${pairIndex}.adjustments.${adjustKey}`}
            control={control}
            render={({ field }) => <SimpleInput {...field} variant="grid" type="number" />}
          />
        )}
      </Td>
    </Tr>
  );
};
